import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import TestCallModal from './TestCallModal';

const DetailedPerformanceTable = ({ agents, onAgentClick }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'resolutionRate', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const itemsPerPage = 10;

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'desc' ? 'asc' : 'desc'
    });
  };

  const handleTestCall = (agent) => {
    setSelectedAgent(agent);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAgent(null);
  };

  const sortedAgents = [...agents]?.sort((a, b) => {
    const aValue = a?.[sortConfig?.key];
    const bValue = b?.[sortConfig?.key];
    
    if (typeof aValue === 'string') {
      return sortConfig?.direction === 'asc' 
        ? aValue?.localeCompare(bValue)
        : bValue?.localeCompare(aValue);
    }
    
    return sortConfig?.direction === 'asc' ? aValue - bValue : bValue - aValue;
  });

  const totalPages = Math.ceil(sortedAgents?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAgents = sortedAgents?.slice(startIndex, startIndex + itemsPerPage);

  const SortIcon = ({ columnKey }) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ChevronsUpDown" size={14} className="text-muted-foreground" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ChevronUp" size={14} className="text-primary" />
      : <Icon name="ChevronDown" size={14} className="text-primary" />;
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-4 md:p-6 border-b border-border">
        <h3 className="text-lg md:text-xl font-semibold text-foreground">Detailed Performance Metrics</h3>
        <p className="text-sm text-muted-foreground mt-1">Comprehensive agent performance breakdown</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead className="bg-muted">
            <tr>
              <th className="text-left p-3 md:p-4">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center gap-2 text-xs md:text-sm font-semibold text-foreground hover:text-primary transition-colors"
                >
                  Agent Name
                  <SortIcon columnKey="name" />
                </button>
              </th>
              <th className="text-center p-3 md:p-4">
                <button
                  onClick={() => handleSort('resolutionRate')}
                  className="flex items-center justify-center gap-2 text-xs md:text-sm font-semibold text-foreground hover:text-primary transition-colors mx-auto"
                >
                  Resolution Rate
                  <SortIcon columnKey="resolutionRate" />
                </button>
              </th>
              <th className="text-center p-3 md:p-4">
                <button
                  onClick={() => handleSort('avgCallTimeMinutes')}
                  className="flex items-center justify-center gap-2 text-xs md:text-sm font-semibold text-foreground hover:text-primary transition-colors mx-auto"
                >
                  Avg Call Time
                  <SortIcon columnKey="avgCallTimeMinutes" />
                </button>
              </th>
              <th className="text-center p-3 md:p-4">
                <button
                  onClick={() => handleSort('totalCalls')}
                  className="flex items-center justify-center gap-2 text-xs md:text-sm font-semibold text-foreground hover:text-primary transition-colors mx-auto"
                >
                  Total Calls
                  <SortIcon columnKey="totalCalls" />
                </button>
              </th>
              <th className="text-center p-3 md:p-4">
                <button
                  onClick={() => handleSort('escalationRate')}
                  className="flex items-center justify-center gap-2 text-xs md:text-sm font-semibold text-foreground hover:text-primary transition-colors mx-auto"
                >
                  Escalation Rate
                  <SortIcon columnKey="escalationRate" />
                </button>
              </th>
              <th className="text-center p-3 md:p-4">
                <button
                  onClick={() => handleSort('inboundPhone')}
                  className="flex items-center justify-center gap-2 text-xs md:text-sm font-semibold text-foreground hover:text-primary transition-colors mx-auto"
                >
                  Inbound Phone
                  <SortIcon columnKey="inboundPhone" />
                </button>
              </th>
              <th className="text-center p-3 md:p-4">
                <button
                  onClick={() => handleSort('outboundPhone')}
                  className="flex items-center justify-center gap-2 text-xs md:text-sm font-semibold text-foreground hover:text-primary transition-colors mx-auto"
                >
                  Outbound Phone
                  <SortIcon columnKey="outboundPhone" />
                </button>
              </th>
              <th className="text-center p-3 md:p-4">
                <button
                  onClick={() => handleSort('whatsappNumber')}
                  className="flex items-center justify-center gap-2 text-xs md:text-sm font-semibold text-foreground hover:text-primary transition-colors mx-auto"
                >
                  WhatsApp Number
                  <SortIcon columnKey="whatsappNumber" />
                </button>
              </th>
              <th className="text-center p-3 md:p-4">
                <button
                  onClick={() => handleSort('createdOn')}
                  className="flex items-center justify-center gap-2 text-xs md:text-sm font-semibold text-foreground hover:text-primary transition-colors mx-auto"
                >
                  Created On
                  <SortIcon columnKey="createdOn" />
                </button>
              </th>
              {/* <th className="text-center p-3 md:p-4">
                <span className="text-xs md:text-sm font-semibold text-foreground">Actions</span>
              </th> */}
            </tr>
          </thead>
          <tbody>
            {paginatedAgents?.map((agent, index) => (
              <tr
                key={agent?.id}
                onClick={() => onAgentClick?.(agent)}
                className={`border-b border-border hover:bg-muted/50 transition-colors cursor-pointer ${
                  index % 2 === 0 ? 'bg-background' : 'bg-muted/20'
                }`}
              >
                <td className="p-3 md:p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${agent?.status === 'active' ? 'bg-success' : 'bg-muted-foreground'}`} />
                    <div>
                      <p className="text-sm font-medium text-foreground">{agent?.name}</p>
                      <p className="text-xs text-muted-foreground">{agent?.id}</p>
                    </div>
                  </div>
                </td>
                <td className="p-3 md:p-4 text-center">
                  <span className="text-sm font-semibold text-foreground">{agent?.resolutionRate}%</span>
                </td>
                <td className="p-3 md:p-4 text-center">
                  <span className="text-sm text-foreground">{agent?.avgCallTime}</span>
                </td>
                <td className="p-3 md:p-4 text-center">
                  <span className="text-sm text-foreground">{agent?.totalCalls?.toLocaleString()}</span>
                </td>
                <td className="p-3 md:p-4 text-center">
                  <span className={`text-sm font-semibold ${agent?.escalationRate > 15 ? 'text-error' : 'text-success'}`}>
                    {agent?.escalationRate}%
                  </span>
                </td>
                <td className="p-3 md:p-4 text-center">
                  <span className="text-sm text-foreground">{agent?.inboundPhone}</span>
                </td>
                <td className="p-3 md:p-4 text-center">
                  <span className="text-sm text-foreground">{agent?.outboundPhone}</span>
                </td>
                <td className="p-3 md:p-4 text-center">
                  <span className="text-sm text-foreground">{agent?.whatsappNumber}</span>
                </td>
                <td className="p-3 md:p-4 text-center">
                  <span className="text-sm text-muted-foreground">{agent?.createdOn}</span>
                </td>
                {/* <td className="p-3 md:p-4">
                  <div className="flex items-center justify-center gap-2">
                    <button
                      className="p-1.5 rounded hover:bg-primary/10 transition-colors"
                      title="View details"
                      onClick={(e) => { e?.stopPropagation(); onAgentClick?.(agent); }}
                    >
                      <Icon name="Eye" size={16} className="text-primary" />
                    </button>
                    <button
                      className="p-1.5 rounded hover:bg-primary/10 transition-colors"
                      title="View conversations"
                    >
                      <Icon name="MessageSquare" size={16} className="text-primary" />
                    </button>
                    <button
                      onClick={(e) => { e?.stopPropagation(); handleTestCall(agent); }}
                      className="px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors"
                      title="Test Call"
                    >
                      Test Call
                    </button>
                  </div>
                </td> */}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="p-4 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedAgents?.length)} of {sortedAgents?.length} agents
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="p-2 rounded border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Icon name="ChevronLeft" size={16} />
          </button>
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-8 h-8 rounded text-sm font-medium transition-colors ${
                    currentPage === pageNum
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted text-foreground'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="p-2 rounded border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Icon name="ChevronRight" size={16} />
          </button>
        </div>
      </div>
      
      {/* Test Call Modal */}
      <TestCallModal 
        isOpen={isModalOpen} 
        onClose={handleCloseModal} 
        agent={selectedAgent} 
      />
    </div>
  );
};

export default DetailedPerformanceTable;